import React, { useState, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import clsx from "clsx";
import QRCode from "qrcode.react";
import CopyIcon from "@material-ui/icons/FileCopyOutlined";
import CheckIcon from "@material-ui/icons/Check";
import EyeIcon from "@material-ui/icons/VisibilityOutlined";
import EyeOffIcon from "@material-ui/icons/VisibilityOffOutlined";
import GearIcon from "@material-ui/icons/SettingsOutlined";
import ChevronIcon from "@material-ui/icons/KeyboardArrowDownRounded";
import AppleIcon from "@material-ui/icons/Apple";
import AndroidIcon from "@material-ui/icons/Android";

// URLs reais das lojas do Numera Fone — preencher em produção.
const APP_STORE_URL = "";
const PLAY_STORE_URL = "";

// URI de provisionamento do Numera Fone: csc:USUARIO:SENHA@DOMINIO
const numeraProvUri = (sipUsername, secret, realm) => `csc:${sipUsername}:${secret}@${realm}`;

const useStyles = makeStyles(theme => ({
  paper: { borderRadius: 16 },
  subtitle: { fontFamily: theme.app.mono, fontSize: 12.5, color: theme.app.textTertiary, margin: "-8px 0 6px" },

  // card do QR (herói)
  qrCard: { border: `1px solid ${theme.app.border}`, borderRadius: 14, padding: "18px 18px 16px", textAlign: "center" },
  qr: { display: "inline-block", lineHeight: 0 },
  qrHint: { fontSize: 12.5, color: theme.app.textSecondary, margin: "12px auto 0", maxWidth: 250, lineHeight: 1.5 },

  // download do app (logo abaixo do QR)
  downloadRow: { display: "flex", justifyContent: "center", gap: 10, marginTop: 16, flexWrap: "wrap" },
  badge: {
    display: "inline-flex", alignItems: "center", gap: 9, background: theme.app.dark, color: "#fff",
    border: "none", borderRadius: 10, padding: "8px 15px", cursor: "pointer", textAlign: "left",
    fontFamily: "inherit", "& svg": { fontSize: 22 }, "&:hover": { background: theme.app.ink },
  },
  badgeText: { display: "flex", flexDirection: "column", lineHeight: 1.15 },
  badgeSmall: { fontSize: 8.5, textTransform: "uppercase", letterSpacing: ".04em", opacity: 0.85 },
  badgeBig: { fontSize: 13, fontWeight: 600 },

  // gatilho do colapsável manual
  manualTrigger: {
    display: "flex", alignItems: "center", gap: 10, width: "100%", border: `1px solid ${theme.app.border}`,
    borderRadius: 11, padding: "11px 14px", background: "#fff", cursor: "pointer", color: theme.app.textSecondary,
    fontSize: 13, fontFamily: "inherit", textAlign: "left", marginTop: 16,
    "& > svg": { fontSize: 18, color: theme.app.textTertiary },
    "&:hover": { background: theme.app.hover },
  },
  manualLabel: { flex: 1 },
  manualChevron: { transition: "transform .2s ease", color: theme.app.textTertiary },
  manualChevronOpen: { transform: "rotate(180deg)" },
  manualBody: { maxHeight: 0, overflow: "hidden", transition: "max-height .28s ease" },
  manualBodyOpen: { maxHeight: 560 },
  manualInner: { paddingTop: 14 },

  // campos (label acima, valor mono, copiar/revelar)
  field: { marginBottom: 12 },
  fieldLabel: { fontSize: 12, fontWeight: 600, color: theme.app.textSecondary, marginBottom: 6 },
  fieldBox: { display: "flex", alignItems: "center", gap: 2, height: 44, border: `1px solid ${theme.app.border}`, borderRadius: 10, padding: "0 6px 0 12px", background: "#fff" },
  fieldValue: { flex: 1, minWidth: 0, fontFamily: theme.app.mono, fontSize: 14, color: theme.app.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  fieldBtn: { color: theme.app.textTertiary, padding: 6, "&:hover": { color: theme.app.ink } },
  ok: { color: theme.app.ok },
  help: { fontSize: 12, color: theme.app.textTertiary, lineHeight: 1.5, marginTop: 4 },
  regen: { marginTop: 12, fontSize: 12, color: theme.app.textSecondary, background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit", textDecoration: "underline", "&:hover": { color: theme.app.danger || "#C0453B" } },

  closeBtn: { background: theme.app.dark, color: "#fff", textTransform: "none", fontWeight: 600, borderRadius: 8, padding: "7px 22px", boxShadow: "none", "&:hover": { background: theme.app.ink, boxShadow: "none" } },
}));

// campo com copiar (feedback ✓) e, opcional, revelar senha
const CredField = ({ label, value, secret }) => {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);
  const [reveal, setReveal] = useState(false);
  const timer = useRef();

  const copy = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(String(value || "")); // copia o valor REAL/completo
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1300);
  };
  const shown = secret && !reveal ? "•".repeat(Math.min(String(value || "").length || 10, 16)) : value;

  return (
    <div className={classes.field}>
      <div className={classes.fieldLabel}>{label}</div>
      <div className={classes.fieldBox}>
        <span className={classes.fieldValue}>{shown}</span>
        {secret && (
          <IconButton size="small" className={classes.fieldBtn} onClick={() => setReveal(r => !r)} title={reveal ? "Ocultar" : "Revelar"}>
            {reveal ? <EyeOffIcon fontSize="small" /> : <EyeIcon fontSize="small" />}
          </IconButton>
        )}
        <IconButton size="small" className={clsx(classes.fieldBtn, copied && classes.ok)} onClick={copy} title="Copiar">
          {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
        </IconButton>
      </div>
    </div>
  );
};

const CredentialsDialog = ({ open, creds, realm, onClose, onRegenerate }) => {
  const classes = useStyles();
  const [manual, setManual] = useState(false);

  const openStore = url => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ className: classes.paper }}>
      <DialogTitle>Credenciais do ramal</DialogTitle>
      {creds && (
        <DialogContent>
          <div className={classes.subtitle}>ramal {creds.sipUsername}</div>

          {/* QR (herói) */}
          <div className={classes.qrCard}>
            {realm ? (
              <div className={classes.qr}>
                <QRCode value={numeraProvUri(creds.sipUsername, creds.secret, realm)} size={186} level="M" includeMargin />
              </div>
            ) : (
              <div className={classes.qrHint}>Domínio (realm) indisponível — use a configuração manual.</div>
            )}
            <div className={classes.qrHint}>Aponte o <b>Numera Fone</b> para este QR para configurar o ramal.</div>

            {/* download do app (precisa do app pra escanear) */}
            <div className={classes.downloadRow}>
              <button type="button" className={classes.badge} onClick={() => openStore(APP_STORE_URL)}>
                <AppleIcon />
                <span className={classes.badgeText}>
                  <span className={classes.badgeSmall}>Baixar na</span>
                  <span className={classes.badgeBig}>App Store</span>
                </span>
              </button>
              <button type="button" className={classes.badge} onClick={() => openStore(PLAY_STORE_URL)}>
                <AndroidIcon />
                <span className={classes.badgeText}>
                  <span className={classes.badgeSmall}>Disponível no</span>
                  <span className={classes.badgeBig}>Google Play</span>
                </span>
              </button>
            </div>
          </div>

          {/* configuração manual (colapsável, fechado por padrão) */}
          <button type="button" className={classes.manualTrigger} onClick={() => setManual(m => !m)}>
            <GearIcon />
            <span className={classes.manualLabel}>Ou configure manualmente em outro softphone</span>
            <ChevronIcon className={clsx(classes.manualChevron, manual && classes.manualChevronOpen)} />
          </button>

          <div className={clsx(classes.manualBody, manual && classes.manualBodyOpen)}>
            <div className={classes.manualInner}>
              <CredField label="Usuário" value={creds.sipUsername} />
              <CredField label="Senha" value={creds.secret} secret />
              <CredField label="Domínio / Realm" value={realm || ""} />
              <div className={classes.help}>
                Usuário e senha são as credenciais do ramal; o domínio é o <b>realm</b>. O servidor/proxy SIP é o
                servidor de voz da plataforma.
              </div>
              {onRegenerate && (
                <button type="button" className={classes.regen} onClick={onRegenerate}>
                  Credencial comprometida? Gerar nova senha
                </button>
              )}
            </div>
          </div>
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={onClose} className={classes.closeBtn}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CredentialsDialog;
